import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { RequiredLabel } from '@/components/RequiredLabel';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useCreateMerchandisingCollection } from '@/hooks/useCreateMerchandisingCollection';
import i18n from '@/lib/i18n';

const formSchema = z.object({
  name: z.string().min(1, {
    message: i18n.t(
      'pages.MerchandisingCollections.components.CreateCollectionModal.form.nameError',
    ),
  }),
  description: z.string().min(1, {
    message: i18n.t(
      'pages.MerchandisingCollections.components.CreateCollectionModal.form.descriptionError',
    ),
  }),
});

type FormData = z.infer<typeof formSchema>;

interface CreateCollectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateCollectionModal({
  open,
  onOpenChange,
}: CreateCollectionModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.components.CreateCollectionModal',
  });

  const {
    formState: { isSubmitting, errors },
    register,
    handleSubmit,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const { createMerchandisingCollection } = useCreateMerchandisingCollection();

  const onSubmit = handleSubmit(async (data) => {
    try {
      await createMerchandisingCollection({
        name: data.name,
        description: data.description,
      });

      toast.success(t('toast.success'));
      reset();
    } catch {
      toast.error(t('toast.error'));
    } finally {
      onOpenChange(false);
    }
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit}>
          <div className="grid gap-4 py-4">
            <FormGroup error={{ message: errors.name?.message, font: 'sm' }}>
              <RequiredLabel htmlFor="name">
                {t('form.nameLabel')}
              </RequiredLabel>

              <Input
                id="name"
                placeholder={t('form.namePlaceholder')}
                {...register('name')}
              />
            </FormGroup>

            <FormGroup
              error={{ message: errors.description?.message, font: 'sm' }}
            >
              <RequiredLabel htmlFor="description">
                {t('form.descriptionLabel')}
              </RequiredLabel>

              <Textarea
                id="description"
                placeholder={t('form.descriptionPlaceholder')}
                rows={3}
                {...register('description')}
              />
            </FormGroup>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('cancelButtonLabel')}
            </Button>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t('form.submitButtonLabelLoading')
                : t('form.submitButtonLabel')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
